import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { io } from "socket.io-client";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ user, children }) => {
  const [socket, setSocket] = useState(null);
  const fallbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  // Use explicit REACT_APP_SOCKET_URL, or fall back to origin, never use /api path
  const socketBase =
    process.env.REACT_APP_SOCKET_URL ||
    fallbackOrigin ||
    "http://localhost:5001";
    
  useEffect(() => {
    // Don't connect until we have a user ID
    if (!user?.id) return;

    // Create the socket connection, passing the userId for room membership
    const socketInstance = io(socketBase, {
      withCredentials: true,
      auth: { userId: user.id },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    setSocket(socketInstance);

    // Basic lifecycle logging to help while learning
    socketInstance.on("connect", () => {
      console.info("[WS] connected", socketInstance.id);
      socketInstance.emit("join", { userId: user.id });
    });

    socketInstance.on("disconnect", (reason) => {
      console.info("[WS] disconnected", reason);
    });

    socketInstance.on("connect_error", (err) => {
      console.error("[WS] connect_error", err.message);
    });

    socketInstance.on("reconnect_attempt", (attemptNumber) => {
      console.info("[WS] reconnect attempt", attemptNumber);
    });

    socketInstance.on("reconnect_failed", () => {
      console.error("[WS] reconnection failed after all attempts");
    });

    // Clean up on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, [user?.id, socketBase]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
